import type { Dispatch, StringWithAutocomplete } from '@monolog/shared/types';
import type { User } from '@monolog/shared/types/database/identity-svc';
import { firstValueFrom, last, tap, catchError, of, finalize } from 'rxjs';
import { useUserStore } from '~/store/user';
import { WebsocketEvents } from '@websockets';
import { ALL } from '@constants';

export const useNotificationStore = defineStore('notification', () => {
  const user = useUserStore();
  const { dispatch, identity } = useGateway();
  const realtime = useRealtime();

  const notifications = reactive<Dispatch.Notification[]>([]);
  const unreadCount = ref(0);
  const searchUsers = ref<User[]>([]);
  const loadingMoreNotifications = ref(false);
  const lastId = ref<string | null>(null);

  const deleteNotification = (notificationId: StringWithAutocomplete<typeof ALL>) => {
    return dispatch.notifications.user.delete(notificationId).pipe(
      tap(() => {
        if (notificationId === ALL) {
          notifications.splice(0);
        } else {
          notifications.splice(
            notifications.findIndex(notification => notification.id === notificationId),
            1,
          );
        }
      }),
    );
  };

  const fetchMoreNotifications = () => {
    if (
      loadingMoreNotifications.value ||
      notifications.length === 0 ||
      lastId.value === notifications[notifications.length - 1].id
    ) {
      return;
    }

    loadingMoreNotifications.value = true;
    lastId.value = notifications[notifications.length - 1].id;

    return dispatch.notifications.user.userList(lastId.value).pipe(
      last(),
      catchError(() => {
        lastId.value = null;

        return of({ data: { notifications: [], unreadCount: 0 } });
      }),
      tap(res => {
        notifications.push(...(res.data.notifications ?? []));
      }),
      finalize(() => {
        loadingMoreNotifications.value = false;
      }),
    );
  };

  const fetchNotifications = (props?: { clear?: boolean }) => {
    return dispatch.notifications.user.userList().pipe(
      last(),
      catchError(() => of({ data: { notifications: [], unreadCount: 0 } })),
      tap(res => {
        if (!res?.data) return;
        if (props?.clear) {
          notifications.splice(0);
          unreadCount.value = 0;
          lastId.value = null;
        }
        notifications.push(...(res.data.notifications ?? []));
        unreadCount.value = res.data.unreadCount ?? 0;
      }),
    );
  };

  const getMentionedUsers = async (search: string, storeId?: string) => {
    const res = await firstValueFrom(identity.users.mentions(search, storeId));
    searchUsers.value = res.data;

    return res.data;
  };

  const handleNotificationCreated = (message: { data: Dispatch.Notification }) => {
    if (notifications.find(notification => notification.id === message.data.id)) return;

    unreadCount.value++;
    notifications.unshift(message.data);
    notifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const initialize = () => {
    if (user.user?.id) {
      realtime.subscribe(user.user.id, WebsocketEvents.Notification.Created, handleNotificationCreated);
    }

    return dispatch.notifications.user.userList().pipe(
      last(),
      catchError(() => of({ data: { notifications: [], unreadCount: 0 } })),
      tap(res => {
        if (!res?.data) return;
        notifications.splice(0);
        notifications.push(...(res.data.notifications ?? []));
        unreadCount.value = res.data.unreadCount ?? 0;
        lastId.value = null;
      }),
    );
  };

  const markAsRead = (notificationId: StringWithAutocomplete<typeof ALL>) => {
    return dispatch.notifications.user.markAsRead(notificationId).pipe(
      tap(res => {
        if (notificationId === ALL) {
          notifications.forEach(notification => {
            notification.updatedAt = new Date().toISOString(); // just dummy date for now, as we don't use it aside for being null or string (boolean)
            unreadCount.value = 0;
          });
        } else {
          const index = notifications.findIndex(notification => notification.id === notificationId);
          if (index >= 0) {
            notifications[index] = res.data;
            unreadCount.value--;
          }
        }
      }),
    );
  };

  return {
    deleteNotification,
    fetchMoreNotifications,
    fetchNotifications,
    getMentionedUsers,
    initialize,
    lastId,
    loadingMoreNotifications,
    markAsRead,
    notifications,
    unreadCount,
  };
});
